import React, { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, FolderOpen, ExternalLink } from "lucide-react";

const KB = 1024;
const MB = 1024 * 1024;

function fmtSize(b) {
  if (!b) return "—";
  if (b < KB) return `${b} B`;
  if (b < MB) return `${(b / KB).toFixed(1)} KB`;
  return `${(b / MB).toFixed(1)} MB`;
}

export default function Portal() {
  const { t } = useLang();
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/documents").then(({ data }) => setDocs(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("contact.error"));
    }
  };

  return (
    <div data-testid="portal-page">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
          <p className="text-sm font-medium uppercase tracking-wider text-blue-700">{t("portal.welcome")}, {user?.name}{user?.company ? ` — ${user.company}` : ""}</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">{t("portal.title")}</h1>
          <p className="mt-3 text-base text-slate-600">{t("portal.subtitle")}</p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        {loading ? (
          <p className="py-16 text-center text-slate-500">{t("common.loading")}</p>
        ) : docs.length === 0 ? (
          <div className="flex flex-col items-center border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="portal-empty">
            <FolderOpen className="mb-4 h-12 w-12 text-slate-300" />
            <p className="max-w-md text-sm text-slate-500">{t("portal.noDocs")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 bg-white">
            <table className="w-full text-left" data-testid="documents-table">
              <thead>
                <tr className="border-b-2 border-slate-800 text-xs uppercase tracking-wider text-slate-600">
                  <th className="px-5 py-3 font-bold">{t("portal.file")}</th>
                  <th className="px-5 py-3 font-bold">{t("portal.machine")}</th>
                  <th className="px-5 py-3 font-bold">{t("portal.size")}</th>
                  <th className="px-5 py-3 font-bold">{t("portal.date")}</th>
                  <th className="px-5 py-3 font-bold"></th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} className="border-b border-slate-200 text-sm hover:bg-slate-50" data-testid={`doc-row-${d.id}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 shrink-0 text-blue-700" />
                        <div>
                          <div className="font-medium text-slate-900">{d.title}</div>
                          <div className="font-mono text-xs text-slate-400">{d.original_filename || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-slate-700">{d.machine || "—"}</td>
                    <td className="px-5 py-4 font-mono text-slate-500">{fmtSize(d.size)}</td>
                    <td className="px-5 py-4 font-mono text-slate-500">{(d.created_at || "").slice(0, 10)}</td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {d.landing_url && (
                          <a href={d.landing_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-none border border-slate-300 px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-700 hover:border-blue-700 hover:text-blue-700" data-testid={`portal-landing-${d.id}`}>
                            <ExternalLink className="h-4 w-4" /> {t("portal.info")}
                          </a>
                        )}
                        {d.original_filename && (
                          <button onClick={() => download(d)} className="inline-flex items-center gap-1 rounded-none bg-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-blue-700" data-testid={`download-${d.id}`}>
                            <Download className="h-4 w-4" /> {t("portal.download")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
